import { Injectable, signal, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { SessionService } from './session.service';
import { LoginService } from './login.service';
import { environment } from '../../environments/environment';
import { BaseLocation, LoginResponse, SessionData, UserPreference } from '../models/user.model';

export interface User {
  id: string | number;
  username: string;
  name: string;
  token: string;
  sessionId: string;
  baseLocationId?: string | number;
  baseLocation?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private http = inject(HttpClient);
  private sessionService = inject(SessionService);
  private loginService = inject(LoginService);

  private _currentUser = signal<User | null>(null);
  private _userDetails: Partial<LoginResponse> = {};

  // Base locations loaded after login
  userBaseLocations = signal<BaseLocation[]>([]);
  // State signals for template driving
  isAuthenticating = signal(false);
  loginSuccess = signal(false); // show base location panel
  copyrightDetails = signal<{ app_version: string; from_year: any; to_year: any } | null>(null);

  constructor() {
    this.loadUserFromSession();
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private loadUserFromSession(): void {
    if (!this.isBrowser) return;
    const token = this.sessionService.token;
    const sessionId = this.sessionService.sessionId;
    const userId = this.sessionService.userId;
    const name = this.sessionService.userName;
    const baseLocationId = this.sessionService.baseLocationId;
    const baseLocation = this.sessionService.baseLocation;

    if (token && sessionId) {
      this._currentUser.set({
        id: userId || '',
        username: name || '',
        name: name || '',
        token,
        sessionId,
        baseLocationId: baseLocationId || undefined,
        baseLocation: baseLocation || undefined
      });
    }
  }

  get user() {
    return this._currentUser;
  }

  isLoggedIn(): boolean {
    if (this._currentUser() !== null) {
      return true;
    }
    if (this.isBrowser && this.sessionService.token && this.sessionService.sessionId) {
      this.loadUserFromSession();
      return this._currentUser() !== null;
    }
    return false;
  }

  getToken(): string | null {
    return this.sessionService.token;
  }

  getSessionId(): string | null {
    return this.sessionService.sessionId;
  }

  getSession(): string | null {
    return this.getSessionId();
  }

  // ─── Login (username + password) ─────────────────────────────────

  async login(username: string, password: string): Promise<{
    success: boolean;
    error?: string;
    userDetails?: LoginResponse;
  }> {
    const moduleId = environment.moduleId;
    const response = await firstValueFrom(
      this.loginService.login({ username, password, module_id: moduleId })
    );

    if (response.status === 200) {
      const data: LoginResponse = response.data;
      this._userDetails = data;

      // Store token in session
      this.sessionService.setTokenDetails(data.token, data.session_id);
      this.sessionService.post('user.id', String(data.user_id ?? (data as any).userId));
      this.sessionService.post('user.name', data.name);

      this._currentUser.set({
        id: data.user_id ?? (data as any).userId,
        username,
        name: data.name,
        token: data.token,
        sessionId: data.session_id
      });

      return { success: true, userDetails: data };
    }

    return { success: false, error: 'Login failed' };
  }

  // ─── Token-based (redirect from another app with ?token=&sessionId=) ─

  async loginWithToken(token: string, sessionId: string): Promise<boolean> {
    this.sessionService.setTokenDetails(token, sessionId);
    this.isAuthenticating.set(true);

    try {
      const res = await firstValueFrom(this.loginService.getSession());
      const sessionData: SessionData[] = res.data;
      if (sessionData.length > 0) {
        const session = sessionData[0];
        this.sessionService.post('session.module_id', String(session.module_id ?? ''));
        this.sessionService.post('user.id', String(session.user_id));
        this.sessionService.post('user.name', session.name);
        this._userDetails = session as any;
        this._currentUser.set({
          id: session.user_id,
          username: session.name,
          name: session.name,
          token,
          sessionId
        });
        return true;
      }
      return false;
    } catch {
      this.isAuthenticating.set(false);
      return false;
    }
  }
  // ─── User geolocation ────────────────────────────────────────────

  setUserLocation(): void {
    if (!this.isBrowser) return;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        this.loginService.setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        }).subscribe({ error: () => { } });
      });
    }
  }

  // ─── Copyright ────────────────────────────────────────────────────

  async loadCopyrightDetails(noAuth = false): Promise<void> {
    // Never run during SSR – the Node server cannot reach the external
    // auth API and the pending promise would delay the SSR response.
    if (!this.isBrowser) return;
    try {
      const endpoint = noAuth
        ? `${environment.entityService}/api/copyright/detailsnoauth`
        : `${environment.entityService}/api/copyright/details`;
      const res: any[] = await firstValueFrom(this.http.get<any[]>(endpoint));
      if (res && res.length) {
        this.copyrightDetails.set(res[0]);
      }
    } catch { /* silently ignore */ }
  }

  // ─── Logout ───────────────────────────────────────────────────────
  // ─── Logout ───────────────────────────────────────────────────────
  async logout(): Promise<void> {
    // 1. Get the correct sessionId up front while the session is still active
    const sessionId = this.sessionService.sessionId;
    const moduleId = this.sessionService.moduleId || String(environment.moduleId);

    // 2. Notify the backend server using the specific session ID if available
    try {
      if (sessionId) {
        await firstValueFrom(this.loginService.removeSessionId(sessionId, moduleId));
      } else {
        await firstValueFrom(this.loginService.logout());
      }
    } catch { /* silently ignore server errors */ }

    // 3. Handle Cross-tab sync for logout events in the browser
    if (this.isBrowser && sessionId) {
      const existingType = localStorage.getItem(`scout_logout_type_${sessionId}`);
      if (existingType !== 'auto') {
        localStorage.setItem(`scout_logout_type_${sessionId}`, 'manual');
      }
      localStorage.setItem(`scout_logout_event_${sessionId}`, Date.now().toString());

      // Remove last activity immediately using the valid sessionId
      localStorage.removeItem(`scout_last_activity_${sessionId}`);

      // Clean up the remaining keys after a short delay so other tabs have time to process the logout
      const cleanup = () => {
        localStorage.removeItem(`scout_logout_event_${sessionId}`);
        localStorage.removeItem(`scout_logout_type_${sessionId}`);
      };

      setTimeout(cleanup, 1500);
      // Ensure cleanup runs even if window.location.href cancels the timeout
      window.addEventListener('pagehide', cleanup, { once: true });
    }

    // 4. Clear memory state and session storage variables
    this.sessionService.clear();
    this._currentUser.set(null);
    this.loginSuccess.set(false);
    this.userBaseLocations.set([]);

    // 5. Safely calculate baseHref and redirect
    let baseHref = '/';
    if (typeof document !== 'undefined') {
      const baseEl = document.querySelector('base');
      if (baseEl) {
        baseHref = baseEl.getAttribute('href') || '/';
      }
    }
    if (!baseHref.endsWith('/')) {
      baseHref += '/';
    }
    window.location.href = baseHref + '#/login';
  }
}
